import React, { useState, useEffect } from "react";
import { Box, FormControl, FormLabel, Select, Input, Button, useToast, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, Text, Table, Thead, Tbody, Tr, Th, Td, Badge, Checkbox, Tooltip, HStack, Tabs, TabList, TabPanels, Tab, TabPanel } from "@chakra-ui/react";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import BulkSalesUpload from "./BulkSalesUpload";
import { isMasterUser, fetchSummary } from "../../../components/newData/useNewData";
import NewData from "./NewData";

const Imports = () => {
  const { selectedRetailer, selectedSupplier } = useSupplierRetailer();
  const [dataType, setDataType] = useState("");
  const [importOption, setImportOption] = useState("");
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // New Data tab (masters only) — badge + tab refetch after every successful
  // upload (ndRefresh), so admins never have to reload the app to see new items.
  const isMaster = typeof isMasterUser === "function" ? isMasterUser() : !!isMasterUser;
  const [newDataPending, setNewDataPending] = useState(0);
  const [ndRefresh, setNdRefresh] = useState(0);
  const onImported = () => {
    setNdRefresh((k) => k + 1);
    // a fresh import with new items should be able to re-show the sign-in popup
    try { sessionStorage.removeItem("spearbi_newdata_dismissed"); } catch (e) {}
  };
  useEffect(() => {
    if (!isMaster) return;
    let cancelled = false;
    fetchSummary()
      .then((s) => {
        if (cancelled || !s || !s.pending) return;
        setNewDataPending((Number(s.pending.store) || 0) + (Number(s.pending.article) || 0));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isMaster, ndRefresh]);

  const [imports, setImports] = useState({
    PNP: [
      { value: "sales", text: "Sales" },
      { value: "sdc", text: "SDC" },
      { value: "articles", text: "Articles" },
      { value: "stores", text: "Stores" },
      { value: "strikerate", text: "Strike Rate" },
    ],
    CHK: [
      { value: "sales", text: "Sales" },
      { value: "sdc", text: "SDC" },
      { value: "dc", text: "DC" },
      { value: "articles", text: "Articles" },
      { value: "stores", text: "Stores" },
    ],
    DIS: [
      { value: "sales", text: "Sales" },
      { value: "sdc", text: "SDC" },//sdc units
      { value: "ranging", text: "Ranging" },//site_article_status
      { value: "articles", text: "Articles" },
      { value: "stores", text: "Stores" },
    ],
    CLI: [
      { value: "Sales", text: "Sales" },
      { value: "sdc", text: "SDC" },
      { value: "dc", text: "DC" },
      { value: "ranging", text: "Ranging" },
      { value: "articles", text: "Articles" },
      { value: "Stores", text: "Stores" },
    ],
    MAK: [
      { value: "sales", text: "Sales" },
      { value: "sdc", text: "SDC" },
      { value: "articles", text: "Articles" },
      { value: "stores", text: "Stores" },
    ],
  });
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (selectedRetailer) {
      setOptions(imports[selectedRetailer] || []);
    }
  }, [selectedRetailer, imports]);

  const handleSubmit = async () => {
    onOpen();
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    setFileName(event.target.files[0]?.name || "");
  };

  const confirmSubmit = async () => {
    onClose();
    setIsLoading(true);

    if (!file) {
      toast({
        title: "Error",
        description: "Please select a file",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
      return;
    }

    let supplier = selectedSupplier; // Replace with your actual supplier value
    const vars = {
      supplier: supplier.toLowerCase(),
      retailer: selectedRetailer.toLowerCase(),
      option:importOption.toLowerCase()
    };

    const formData = new FormData();
    formData.append("xlsxFile", file);
    Object.keys(vars).forEach((key) => {
      formData.append(key, vars[key]);
    });
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/data/uploads/`, {
        method: "POST",
        body: formData,
      });
      const status = response.status;
      const data = await response.json();
      console.log({ data });
      if (status === 201) {
        toast({
          title: "Success",
          description: data.message,
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        if (isMaster) onImported();
      } else {
        toast({
          title: "Error",
          description: data.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error during file upload:", error);
      toast({
        title: "Error",
        description: `Exception occurred during file upload: ${error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  const isBulkSales = importOption.toLowerCase() === "sales" && ["PNP", "CHK", "DIS", "CLI"].includes(selectedRetailer);

  const uploadForm = (
    <Box maxW={isBulkSales ? "1000px" : "500px"} mx="auto" mt={8} p={4} borderWidth="1px" borderRadius="lg">
      <FormControl mb={4}>
        <FormLabel>Selected Retailer: {selectedRetailer}</FormLabel>
      </FormControl>
      <FormControl mb={4} isRequired>
        <FormLabel>Data Type</FormLabel>
        <Select placeholder="Select data type" value={importOption} onChange={(e) => setImportOption(e.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </Select>
      </FormControl>

      {isBulkSales ? (
        <BulkSalesUpload retailer={selectedRetailer} supplier={selectedSupplier} onImported={isMaster ? onImported : undefined} />
      ) : (
        <>
      <FormControl mb={4} isRequired>
        <FormLabel>Upload File</FormLabel>
        <Input type="file" accept=".xlsx" onChange={handleFileChange} />
      </FormControl>
      <Button colorScheme="blue" onClick={handleSubmit} isLoading={isLoading}>
        Submit
      </Button>
        </>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Upload</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              <strong>Retailer:</strong> {selectedRetailer}
            </Text>
            <Text>
              <strong>Data Type:</strong> {importOption}
            </Text>
            <Text>
              <strong>File:</strong> {fileName}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={confirmSubmit}>
              Confirm
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );

  if (!isMaster) {
    return uploadForm;
  }

  return (
    <Tabs isLazy lazyBehavior="keepMounted" variant="line">
      <TabList>
        <Tab>Upload</Tab>
        <Tab>
          New Data
          {newDataPending > 0 && (
            <Badge ml={2} borderRadius="full" colorScheme="orange">
              {newDataPending}
            </Badge>
          )}
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel px={0}>{uploadForm}</TabPanel>
        <TabPanel px={0}>
          <NewData refreshKey={ndRefresh} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default Imports;
